import { useEffect, useCallback } from 'react';

/**
 * useRouteManagement Hook
 *
 * Manages route selection and provides route-related helper functions.
 * Handles auto-selection of single route, pickup/dropoff location helpers,
 * and route change with dependent state reset.
 *
 * State Ownership:
 * - Reads: routes from parent
 * - Updates: booking state via callbacks
 * - Manages: route selection logic, location filtering
 *
 * Integration Points:
 * - Reads: routes, booking from parent
 * - Updates: booking, selectedDates, availableTimeSlots, bookAllAvailableDates, normalizedBookingTime
 * - Calls: fetchAppliedSchedules for route schedules
 *
 * @param {Object} params
 * @param {Array} params.routes - List of available routes
 * @param {Object} params.booking - Current booking state
 * @param {Function} params.setBooking - Update booking state
 * @param {Function} params.setSelectedDates - Reset selected dates
 * @param {Function} params.setAvailableTimeSlots - Reset available time slots
 * @param {Function} params.setBookAllAvailableDates - Reset book all dates flag
 * @param {Function} params.setNormalizedBookingTime - Reset normalized time
 * @param {Function} params.fetchAppliedSchedules - Fetch schedules for route
 * @returns {Object} Route management functions
 */
const useRouteManagement = ({
  routes,
  booking,
  setBooking,
  setSelectedDates,
  setAvailableTimeSlots,
  setBookAllAvailableDates,
  setNormalizedBookingTime,
  fetchAppliedSchedules
}) => {
  /**
   * Get current selected route object
   */
  const getCurrentRoute = useCallback(() => {
    return routes.find(route => route.id.toString() === booking.route);
  }, [routes, booking.route]);

  /**
   * Get pickup locations for a route
   * Returns sorted list of waypoints that allow pickup
   */
  const getPickupLocations = useCallback((route) => {
    if (!route?.waypoints) return [];

    return route.waypoints
      .filter(waypoint => waypoint.can_pickup)
      .sort((a, b) => a.sequence_number - b.sequence_number)
      .map(waypoint => ({
        id: waypoint.id,
        name: waypoint.address,
        sequence_number: waypoint.sequence_number,
        isStartLocation: waypoint.sequence_number === 0
      }));
  }, []);

  /**
   * Get dropoff locations for a route
   * Returns sorted list of waypoints that allow dropoff
   */
  const getDropoffLocations = useCallback((route) => {
    if (!route?.waypoints) return [];

    return route.waypoints
      .filter(waypoint => waypoint.can_dropoff)
      .sort((a, b) => a.sequence_number - b.sequence_number)
      .map(waypoint => ({
        id: waypoint.id,
        name: waypoint.address,
        sequence_number: waypoint.sequence_number,
        isEndLocation: waypoint.sequence_number === route.waypoints.length - 1
      }));
  }, []);

  /**
   * Handle route change event
   * Resets dependent state and auto-selects pickup/dropoff if no intermediate stops
   */
  const handleRouteChange = useCallback(async (e) => {
    const selectedRoute = routes.find(route => route.id.toString() === e.target.value);
    const routeId = e.target.value;

    if (!selectedRoute) {
      console.log('No route found for id:', routeId);
      return;
    }

    // Reset date and time selections (dependent state)
    setSelectedDates([]);
    setAvailableTimeSlots([]);
    setBookAllAvailableDates(false);
    setNormalizedBookingTime(null);

    // Log stops for debugging
    console.log('Selected route stops:', selectedRoute.waypoints);

    // Automatically set pickup and dropoff if no intermediate stops
    const stops = selectedRoute.waypoints.filter(waypoint => waypoint.point_type === 'stop') || [];
    const hasIntermediateStops = stops.length > 2;

    // Update booking state with route and auto-selected locations
    setBooking(prev => ({
      ...prev,
      route: routeId,
      date: null,
      schedule: '',
      pickup: !hasIntermediateStops && stops.length >= 1 ? stops[0].id.toString() : '',
      dropoff: !hasIntermediateStops && stops.length >= 2 ? stops[1].id.toString() : '',
    }));

    // Fetch schedules for the selected route
    if (routeId) {
      // Pass false to prevent showing full-page loading state which causes scroll jump
      await fetchAppliedSchedules(routeId, false);
    }
  }, [routes, setBooking, setSelectedDates, setAvailableTimeSlots, setBookAllAvailableDates, setNormalizedBookingTime, fetchAppliedSchedules]);

  /**
   * Auto-select single route on mount
   * If only one route available, automatically select it and fetch its schedules
   */
  useEffect(() => {
    if (routes.length === 1 && !booking.route) {
      const singleRoute = routes[0];
      console.log('Auto-selecting single route:', singleRoute);

      // Check for intermediate stops
      const stops = singleRoute.waypoints?.filter(waypoint => waypoint.point_type === 'stop') || [];
      const hasIntermediateStops = stops.length > 2;

      console.log('Single route analysis:', {
        totalWaypoints: singleRoute.waypoints?.length,
        stops: stops.length,
        hasIntermediateStops
      });

      setBooking(prev => ({
        ...prev,
        route: singleRoute.id.toString(),
        // Auto-set pickup and dropoff if no intermediate stops
        pickup: !hasIntermediateStops && stops.length >= 1 ? stops[0].id.toString() : '',
        dropoff: !hasIntermediateStops && stops.length >= 2 ? stops[1].id.toString() : '',
      }));

      // Fetch schedules for the single route (don't show full-page loading)
      fetchAppliedSchedules(singleRoute.id, false);
    }
  }, [routes, booking.route, setBooking, fetchAppliedSchedules]);

  return {
    getCurrentRoute,
    getPickupLocations,
    getDropoffLocations,
    handleRouteChange,
  };
};

export default useRouteManagement;
